/** @prettier */

import { useCallback, useEffect, useState } from 'react';
import { useTimeout } from '@surfline/quiver-react/dist/hooks/useTimeout';

import { useToggle } from '../../../hooks/useToggle';
import { IS_BASIC } from '../../../selectors/user';

/**
 * A custom hook for creating an AD timer, this hook will run an ad every 30 seconds by default for a `userType === 'isBasic'`
 * if the `isPlaying` prop is true. It will run a pre-roll ad when a user toggles `isPlaying` from `false` to `true` and then a
 * mid roll every 30 seconds thereafter.
 *
 * @param {Object} props
 * @param {Boolean} props.hasCam Boolean determining if the user has a spot with a cam selected in their multi cam setup
 * @param {Boolean} props.isPlaying Boolean multi cam playing status
 * @param {function} props.setCompanionAdKey Function used to increment the companion ad key
 * to render a new ad everytime a pre/mid roll ad plays
 * @param {number} props.numCams
 * @param {["isBasic"|"anonymous"|"isPremium"]} props.userType Boolean multi cam playing status
 */
export const useAdTimer = ({ hasCam, isPlaying, userType, setCompanionAdKey, numCams }) => {
  const adInterval = 30000;
  const isBasicUser = userType === IS_BASIC;
  const [showUpSell, setShowUpSell] = useState(false);
  const [isPlayingAd, setAdPlaying] = useToggle(false);

  /* Only play the ad if it is a basic user and they have a cam in their setup */
  const doPlayAd = useCallback(() => {
    if (isBasicUser && hasCam) {
      setShowUpSell(false);
      setAdPlaying(true);
    }
  }, [hasCam, isBasicUser, setAdPlaying]);

  const adTimerCallback = useCallback(() => {
    setShowUpSell(true);
  }, []);
  const startAdTimer = isPlaying && isBasicUser && !isPlayingAd && !showUpSell && numCams !== 9;

  useTimeout({
    timeoutLength: adInterval,
    triggers: [isPlayingAd],
    initialState: false,
    cb: adTimerCallback,
    start: startAdTimer,
  });

  /* If a free user pauses a video, they get an ad upon resuming like the spot pages */
  useEffect(() => {
    if (isPlaying && isBasicUser) {
      // Fire Pre Roll
      doPlayAd();
    }
  }, [doPlayAd, isBasicUser, isPlaying, setCompanionAdKey]);

  /* Everytime we play an ad, re-render the companion ad */
  useEffect(() => {
    if (isPlayingAd) {
      setCompanionAdKey(curr => curr + 1);
    }
  }, [isPlayingAd, setCompanionAdKey]);

  const onUpSellComplete = useCallback(() => {
    setShowUpSell(false);
    doPlayAd();
  }, [doPlayAd]);

  const onAdComplete = useCallback(() => {
    setAdPlaying(false);
  }, [setAdPlaying]);

  return { onAdComplete, doPlayAd, isPlayingAd, showUpSell, onUpSellComplete, setAdPlaying };
};
